import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import {
  formatFrenchDate,
  getAppointmentDisplayDate,
  getQuoteProblemName,
  getQuoteServiceName,
} from '../appointmentTiming';
import { createQuotePdfBlobUrl, downloadQuotePdf } from '../../../utils/quotePdf';
import QuoteRefuseModal from './QuoteRefuseModal';

const BOOKING_DRAFT_KEY = 'plumberpro_booking_draft';

const formatPrice = (price) =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(Number(price) || 0);

const formatValidUntilLabel = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return formatFrenchDate(value);

  const dateLabel = date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const timeLabel = date.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${dateLabel} à ${timeLabel}`;
};

/** Ex. 12/09/2026 à 18h00 */
const formatExpiredSinceLabel = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return formatFrenchDate(value);

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} à ${hours}h${minutes}`;
};

const isQuotePastValidity = (quote) => {
  if (!quote?.valid_until) return false;
  const until = new Date(quote.valid_until);
  if (Number.isNaN(until.getTime())) return false;
  return until.getTime() < Date.now();
};

const CLOSED_QUOTE_STATUSES = new Set(['converted', 'closed', 'completed']);

const getQuoteStatusLabel = (quote, hasQuote, isExpired) => {
  const status = `${quote?.status || ''}`.toLowerCase();

  if (CLOSED_QUOTE_STATUSES.has(status)) return 'Clôturé';
  if (status === 'accepted') return 'Devis accepté';
  if (status === 'rejected' || status === 'refused') return 'Devis refusé';
  if (isExpired) return 'Devis expiré';
  if (hasQuote) return 'Devis disponible';
  return 'Devis en cours';
};

const DetailRow = ({ icon, label, children }) => {
  if (children == null || children === '') return null;
  return (
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
        <Icon name={icon} size={20} className="text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap break-words">
          {children}
        </p>
      </div>
    </div>
  );
};

const QuoteDetailsPanel = ({
  appointment,
  quote = null,
  quoteLoading = false,
  quoteError = null,
  onRetryQuote,
  onQuoteDecision,
  onAcceptBooking,
  onContinueBooking,
}) => {
  const navigate = useNavigate();
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfError, setPdfError] = useState(null);
  const [decisionLoading, setDecisionLoading] = useState(false);
  const [decisionError, setDecisionError] = useState(null);
  const [showRefuseModal, setShowRefuseModal] = useState(false);

  const requestDate = formatFrenchDate(getAppointmentDisplayDate(appointment));
  const problemName = getQuoteProblemName(appointment);
  const serviceName = getQuoteServiceName(appointment);
  const hasNotes = Boolean(appointment?.notes?.trim());
  const validUntil = formatFrenchDate(quote?.valid_until);
  const validUntilLabel = formatValidUntilLabel(quote?.valid_until);
  const expiredSinceLabel = formatExpiredSinceLabel(quote?.valid_until);
  const hasQuote = Boolean(quote?.id || quote?.reference);
  const quoteStatus = `${quote?.status || ''}`.toLowerCase();
  const isBookedIntervention =
    Boolean(appointment) &&
    !appointment?.isQuote &&
    !appointment?.is_quote &&
    (quoteStatus === 'accepted' || CLOSED_QUOTE_STATUSES.has(quoteStatus));
  const isClosedQuote = CLOSED_QUOTE_STATUSES.has(quoteStatus) || isBookedIntervention;
  const isTerminalStatus =
    quoteStatus === 'accepted' ||
    quoteStatus === 'rejected' ||
    quoteStatus === 'refused' ||
    isClosedQuote;
  const isExpired = hasQuote && !isTerminalStatus && isQuotePastValidity(quote);
  const canDecide =
    hasQuote &&
    quote?.id &&
    !isClosedQuote &&
    !isExpired &&
    quoteStatus !== 'accepted' &&
    quoteStatus !== 'rejected' &&
    quoteStatus !== 'refused';
  const canContinueBooking =
    quoteStatus === 'accepted' &&
    !isClosedQuote &&
    Boolean(onContinueBooking || onAcceptBooking);

  const totalHt = Number(quote?.total_ht ?? quote?.price) || 0;
  const totalTtc = Number(quote?.total_ttc) || 0;
  const vatAmount =
    totalTtc > 0 && totalHt > 0
      ? Math.max(0, totalTtc - totalHt)
      : totalHt * ((Number(quote?.vat_rate) || 20) / 100);

  useEffect(() => {
    let objectUrl = null;
    setPdfError(null);

    if (!hasQuote || !appointment) {
      setPdfUrl(null);
      return undefined;
    }

    try {
      objectUrl = createQuotePdfBlobUrl(appointment, quote);
      setPdfUrl(objectUrl);
    } catch (err) {
      console.error('Erreur generation PDF devis:', err);
      setPdfError(err?.message || 'Impossible de générer le PDF du devis');
      setPdfUrl(null);
    }

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [appointment, quote, hasQuote]);

  const handleRequestNewQuote = () => {
    try {
      localStorage.removeItem(BOOKING_DRAFT_KEY);
    } catch {
      // ignore
    }
    navigate('/service-booking-wizard', { state: {} });
  };

  const handleAcceptClick = () => {
    if (decisionLoading) return;
    setDecisionError(null);
    if (onAcceptBooking) {
      onAcceptBooking();
      return;
    }
    if (onContinueBooking) {
      onContinueBooking();
    }
  };

  const handleDecision = async (accepted) => {
    if (!accepted) {
      if (!onQuoteDecision || !quote?.id || decisionLoading) return;
      setShowRefuseModal(true);
      return;
    }
    handleAcceptClick();
  };

  const handleConfirmRefuse = async (refusalPayload) => {
    if (!onQuoteDecision || !quote?.id || decisionLoading) return;

    setDecisionLoading(true);
    setDecisionError(null);

    try {
      await onQuoteDecision('rejected', refusalPayload);
      setShowRefuseModal(false);
    } catch (err) {
      console.error('Erreur refus devis:', err);
      setDecisionError(err?.message || 'Impossible d’enregistrer le refus du devis');
    } finally {
      setDecisionLoading(false);
    }
  };

  const statusLabel = getQuoteStatusLabel(quote, hasQuote, isExpired);
  const referenceLabel = quote?.reference || appointment?.reference || null;

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-lg p-6 shadow-sm border border-border">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <h2 className="text-sm font-medium text-foreground">
            {hasQuote
              ? requestDate
                ? `Devis du ${requestDate}`
                : 'Votre devis'
              : requestDate
                ? `Demande de devis du ${requestDate}`
                : 'Votre demande de devis'}
          </h2>
          {hasQuote && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              iconName="Download"
              iconPosition="left"
              onClick={() => downloadQuotePdf(appointment, quote)}
              className="self-start sm:self-auto"
            >
              Télécharger le devis
            </Button>
          )}
        </div>

        <div className="space-y-4">
          <DetailRow icon="Hash" label="Référence">
            {referenceLabel}
          </DetailRow>

          <DetailRow icon="Activity" label="Statut">
            {statusLabel}
          </DetailRow>

          <DetailRow icon="AlertCircle" label="Problème">
            {problemName}
          </DetailRow>

          <DetailRow icon="Wrench" label="Solution proposée">
            {serviceName}
          </DetailRow>

          <DetailRow icon="FileText" label="Montant TTC">
            {hasQuote ? formatPrice(quote.total_ttc) : null}
          </DetailRow>

          {hasQuote && validUntil && (
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name="Clock" size={20} className="text-primary" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">Valable jusqu&apos;au</p>
                <p className="text-sm text-muted-foreground mt-1">{validUntil}</p>
              </div>
            </div>
          )}

          {hasNotes && !hasQuote && (
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name="MessageSquare" size={20} className="text-primary" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">Précisions</p>
                <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{appointment.notes}</p>
              </div>
            </div>
          )}

          {requestDate && (
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name="Calendar" size={20} className="text-primary" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">Date de la demande</p>
                <p className="text-sm text-muted-foreground mt-1">{requestDate}</p>
              </div>
            </div>
          )}

          {appointment?.customerAddress && (
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name="MapPin" size={20} className="text-primary" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">Adresse</p>
                <p className="text-sm text-muted-foreground mt-1">{appointment.customerAddress}</p>
              </div>
            </div>
          )}
        </div>

        {!hasQuote && !quoteLoading && !quoteError && (
          <div className="mt-6 p-4 rounded-lg bg-muted/40 border border-border">
            <p className="text-sm text-muted-foreground">
              Un technicien étudie votre demande et vous contactera pour vous transmettre un devis personnalisé.
            </p>
          </div>
        )}
      </div>

      {quoteLoading && (
        <div className="bg-card rounded-lg p-6 shadow-sm border border-border flex items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Chargement du devis…</p>
        </div>
      )}

      {(quoteError || pdfError) && (
        <div className="bg-card rounded-lg p-6 shadow-sm border border-error/30">
          <div className="flex items-start gap-3">
            <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">Impossible d&apos;afficher le devis</p>
              <p className="text-sm text-muted-foreground mt-1">{quoteError || pdfError}</p>
              {onRetryQuote && (
                <Button type="button" variant="outline" size="sm" className="mt-3" onClick={onRetryQuote}>
                  Réessayer
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {hasQuote && (
        <div className="bg-card rounded-2xl p-6 sm:p-8 shadow-sm border border-border max-w-lg mx-auto w-full">
          <div className="space-y-3 pb-5 border-b border-border">
            <div className="flex items-center justify-between gap-4 text-sm sm:text-base">
              <span className="text-muted-foreground">Intervention</span>
              <span className="font-medium text-foreground tabular-nums">{formatPrice(totalHt)}</span>
            </div>
            <div className="flex items-center justify-between gap-4 text-sm sm:text-base">
              <span className="text-muted-foreground">TVA</span>
              <span className="font-medium text-foreground tabular-nums">{formatPrice(vatAmount)}</span>
            </div>
            <div className="flex items-center justify-between gap-4 pt-1">
              <span className="text-sm sm:text-base font-semibold uppercase tracking-wide text-foreground">
                Total TTC
              </span>
              <span className="text-lg sm:text-xl font-bold text-foreground tabular-nums">
                {formatPrice(totalTtc || totalHt + vatAmount)}
              </span>
            </div>
          </div>

          {validUntilLabel && (
            <div className="py-5 border-b border-border">
              <p className="text-sm text-muted-foreground">Valable jusqu&apos;au</p>
              <p className="text-base font-medium text-foreground mt-1">{validUntilLabel}</p>
            </div>
          )}

          <div className="pt-5">
            <Button
              type="button"
              variant="outline"
              fullWidth
              iconName="Download"
              iconPosition="left"
              onClick={() => downloadQuotePdf(appointment, quote)}
              className="mb-5"
            >
              Télécharger le devis PDF
            </Button>

            {canDecide ? (
              <div className="space-y-4">
                <Button
                  type="button"
                  variant="success"
                  size="xl"
                  fullWidth
                  disabled={decisionLoading}
                  loading={decisionLoading}
                  iconName="Check"
                  iconPosition="left"
                  onClick={() => handleDecision(true)}
                  className="uppercase tracking-wide"
                >
                  Accepter le devis
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    disabled={decisionLoading}
                    onClick={() => handleDecision(false)}
                    className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline disabled:opacity-50"
                  >
                    Refuser le devis
                  </button>
                </div>
              </div>
            ) : isExpired ? (
              <div className="space-y-4 text-center">
                <p className="text-sm sm:text-base text-foreground leading-relaxed">
                  Ce devis n&apos;est malheureusement plus valide depuis le{' '}
                  <span className="font-medium">{expiredSinceLabel || validUntil}</span>.
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Si vous souhaitez toujours réaliser cette intervention, vous pouvez vous rendre sur
                  notre site pour demander un nouveau devis.
                </p>
                <Button
                  type="button"
                  variant="default"
                  size="xl"
                  fullWidth
                  iconName="ArrowRight"
                  iconPosition="right"
                  onClick={handleRequestNewQuote}
                >
                  Demander un devis
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground text-center">
                  {isClosedQuote
                    ? 'Ce devis est clôturé suite à la réservation. Aucune action supplémentaire n’est possible.'
                    : quoteStatus === 'accepted'
                      ? 'Vous avez accepté ce devis.'
                      : quoteStatus === 'rejected' || quoteStatus === 'refused'
                        ? 'Vous avez refusé ce devis.'
                        : null}
                </p>
                {canContinueBooking && (
                  <Button
                    type="button"
                    variant="default"
                    size="xl"
                    fullWidth
                    iconName="ArrowRight"
                    iconPosition="right"
                    onClick={onAcceptBooking || onContinueBooking}
                  >
                    Choisir un créneau
                  </Button>
                )}
              </div>
            )}

            {decisionError && (
              <p className="text-sm text-error mt-4 text-center">{decisionError}</p>
            )}
          </div>
        </div>
      )}

      <QuoteRefuseModal
        open={showRefuseModal}
        quote={quote}
        loading={decisionLoading}
        onClose={() => {
          if (!decisionLoading) setShowRefuseModal(false);
        }}
        onConfirm={handleConfirmRefuse}
      />
    </div>
  );
};

export default QuoteDetailsPanel;
